import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar as CalendarIcon, CheckSquare, Video, Users, Globe,
  PenLine, Clock, User as UserIcon, LayoutGrid, ChevronRight, Eye,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import UserAvatar from '@/components/common/UserAvatar';
import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import type { UnifiedEvent } from './types';

type IconComponent = ComponentType<LucideProps>;

interface Props {
  events: UnifiedEvent[];
  statusColors: Record<string, { bg: string; text: string; border: string }>;
  statusIcons: Record<string, IconComponent>;
  onEventClick: (e: UnifiedEvent) => void;
  onOpenInPlanning?: (e: UnifiedEvent) => void;
}

function sourceIcon(s: UnifiedEvent['source']): IconComponent {
  if (s === 'appointment') return CalendarIcon;
  if (s === 'task') return CheckSquare;
  if (s === 'videocall') return Video;
  if (s === 'personal') return UserIcon;
  return Users;
}

function originIcon(evt: UnifiedEvent): { Icon: IconComponent; label: string } {
  const meta = (evt.metadata || {}) as Record<string, unknown>;
  if (evt.appointment_form_id || meta.source === 'public_form' || meta.appointment_form_id) {
    return { Icon: Globe, label: 'Da form pubblico' };
  }
  if (meta.googleEventId || meta.google_event_id) {
    return { Icon: Globe, label: 'Da Google Calendar' };
  }
  return { Icon: PenLine, label: 'Inserito manualmente' };
}

export default function CockpitEventList({
  events, statusColors, statusIcons, onEventClick, onOpenInPlanning,
}: Props) {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 flex items-center justify-center mb-3">
          <span className="text-2xl">☕</span>
        </div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Giornata libera</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Goditela, oppure pianifica un nuovo appuntamento.</p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <ul className="space-y-2">
        <AnimatePresence mode="popLayout" initial={false}>
          {events.map((evt, idx) => {
            const SourceIcon = sourceIcon(evt.source);
            const sc = statusColors[evt.status];
            const StatusIcon = statusIcons[evt.status];
            const { Icon: OriginIcon, label: originLabel } = originIcon(evt);
            const accent = evt.source === 'appointment' ? (evt.type_color || '#6366f1')
              : evt.source === 'task' ? '#f59e0b'
              : evt.source === 'workgroup_event' ? '#8b5cf6'
              : evt.source === 'personal' ? (evt.metadata?.color || evt.type_color || '#0ea5e9')
              : '#22c55e';
            const customer = evt.metadata?.customerName
              || evt.metadata?.creatorName
              || evt.metadata?.workgroupName;

            return (
              <motion.li
                key={`${evt.source}-${evt.id}`}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ delay: Math.min(idx, 8) * 0.025, type: 'spring', stiffness: 380, damping: 30 }}
                role="button"
                tabIndex={0}
                aria-label={`${evt.title}${evt.start_time ? ` alle ${evt.start_time}` : ''}`}
                onClick={() => onEventClick(evt)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onEventClick(evt);
                  }
                }}
                className={cn(
                  'group relative flex items-stretch gap-0 rounded-xl border bg-white dark:bg-gray-900 cursor-pointer overflow-hidden',
                  'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900',
                )}
              >
                <div
                  className="w-1 flex-shrink-0"
                  style={{ backgroundColor: accent }}
                />
                <div className="px-3 py-2.5 flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex flex-col items-center justify-center w-14 flex-shrink-0">
                    <div className="text-base font-semibold text-gray-900 dark:text-white tabular-nums leading-tight">
                      {evt.start_time || '—'}
                    </div>
                    {evt.end_time && (
                      <div className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums leading-tight">
                        {evt.end_time}
                      </div>
                    )}
                  </div>

                  <UserAvatar
                    id={evt.assigned_to_id || undefined}
                    name={evt.assigned_to_name || undefined}
                    avatarUrl={evt.assigned_to_avatar_url || undefined}
                    size="sm"
                  />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <SourceIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: accent }} />
                      <h5 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{evt.title}</h5>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 min-w-0">
                      {customer && (
                        <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1 truncate">
                          <UserIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{customer}</span>
                        </span>
                      )}
                      {evt.type_name && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate">{evt.type_name}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <OriginIcon className="w-3.5 h-3.5 text-gray-400" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">{originLabel}</TooltipContent>
                    </Tooltip>
                    {sc && (
                      <span className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border',
                        sc.bg, sc.text, sc.border,
                      )}>
                        {StatusIcon && <StatusIcon className="w-2.5 h-2.5" />}
                        <span className="hidden sm:inline">{evt.status_label}</span>
                      </span>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onEventClick(evt); }}
                          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                          aria-label="Apri dettagli"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Apri dettagli</TooltipContent>
                    </Tooltip>
                    {onOpenInPlanning && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onOpenInPlanning(evt); }}
                            className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                            aria-label="Apri in Planning"
                          >
                            <LayoutGrid className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Apri in Planning</TooltipContent>
                      </Tooltip>
                    )}
                    <ChevronRight className="hidden sm:inline-block w-3.5 h-3.5 text-gray-300 dark:text-gray-600 sm:group-hover:hidden" />
                  </div>
                </div>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    </TooltipProvider>
  );
}

export function CockpitAllDayEvents({
  events, onEventClick,
}: { events: UnifiedEvent[]; onEventClick: (e: UnifiedEvent) => void }) {
  if (events.length === 0) return null;
  return (
    <div className="mb-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/20 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-4 h-4 text-amber-600" />
        <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">
          Tutto il giorno · {events.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {events.map(evt => (
          <button
            key={`allday-${evt.source}-${evt.id}`}
            type="button"
            onClick={() => onEventClick(evt)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 text-xs text-gray-800 dark:text-gray-100 hover:shadow-sm transition"
          >
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            {evt.title}
          </button>
        ))}
      </div>
    </div>
  );
}
